import { useHead } from '@unhead/vue';
import { unref, computed } from 'vue';

const BASE_URL = 'https://lokerjambi.co.id';
const SITE_NAME = 'Lokerjambi.co.id';
const DEFAULT_DESCRIPTION =
  'Platform lowongan kerja terpercaya di Jambi. Temukan ribuan lowongan kerja terbaru dari perusahaan terkemuka di Jambi dan sekitarnya.';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;

/**
 * Composable untuk mengatur SEO meta tags pada setiap halaman.
 * Gunakan di dalam <script setup> setiap view.
 *
 * @param {object} options
 * @param {string|import('vue').Ref<string>} options.title - Judul halaman (tanpa suffix site name)
 * @param {string|import('vue').Ref<string>} [options.description] - Meta description halaman
 * @param {string|import('vue').Ref<string>} [options.path] - Path URL halaman, contoh: '/about'
 * @param {string|import('vue').Ref<string>} [options.image] - URL gambar OG
 * @param {'website'|'article'|import('vue').Ref<string>} [options.type] - OG type
 * @param {object|import('vue').Ref<object>} [options.jsonLd] - Structured data JSON-LD (opsional)
 */
export function useSeoMeta(options = {}) {
  const headConfig = computed(() => {
    const title = unref(options.title);
    const description = unref(options.description) || DEFAULT_DESCRIPTION;
    const path = unref(options.path) || '';
    const image = unref(options.image) || DEFAULT_IMAGE;
    const type = unref(options.type) || 'website';
    const jsonLd = unref(options.jsonLd) || null;

    const fullTitle = title ? `${title} — ${SITE_NAME}` : SITE_NAME;
    const canonicalUrl = `${BASE_URL}${path}`;

    const config = {
      title: fullTitle,
      meta: [
        // Standard
        { name: 'description', content: description },

        // Open Graph
        { property: 'og:type', content: type },
        { property: 'og:title', content: fullTitle },
        { property: 'og:description', content: description },
        { property: 'og:image', content: image },
        { property: 'og:url', content: canonicalUrl },
        { property: 'og:site_name', content: SITE_NAME },
        { property: 'og:locale', content: 'id_ID' },

        // Twitter Card
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: fullTitle },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: image },
      ],
      link: [{ rel: 'canonical', href: canonicalUrl }],
    };

    // Inject JSON-LD structured data jika ada
    if (jsonLd) {
      config.script = [
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(jsonLd),
          key: 'json-ld',
        },
      ];
    }

    return config;
  });

  useHead(headConfig);
}

/**
 * Menghasilkan JSON-LD WebSite schema untuk halaman utama.
 */
export function getWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: BASE_URL,
    description: DEFAULT_DESCRIPTION,
    inLanguage: 'id-ID',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
    publisher: {
      '@type': 'Organization',
      name: 'PT Uwais Digital Hired',
      url: BASE_URL,
      logo: {
        '@type': 'ImageObject',
        url: `${BASE_URL}/favicon.ico`,
      },
    },
  };
}

/**
 * Menghasilkan JSON-LD Organization schema.
 */
export function getOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'PT Uwais Digital Hired',
    alternateName: SITE_NAME,
    url: BASE_URL,
    logo: `${BASE_URL}/favicon.ico`,
    sameAs: [],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      availableLanguage: 'Indonesian',
    },
  };
}

/**
 * Menghasilkan JSON-LD FAQPage schema dari array FAQ.
 * @param {Array<{question: string, answer: string}>} faqs
 */
export function getFaqPageSchema(faqs = []) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}
